// AnnouncementPopupModal.jsx
import React, { useState } from "react";
import { Modal } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { doc, setDoc } from "firebase/firestore";
import { useQueryClient } from "@tanstack/react-query";
import ChantButton from "../../../CommonComponents/ChantButton/ChantButton";
import "./AnnouncementPopupModal.css";
import { useNavigate } from "react-router-dom";
import useGlobalStore from "../../../store/store";
import { db } from "../../../services/firebaseService/connection";
import useUserStore, { selectUserId } from "../../../store/user/user";
import useAdminDataStore, { selectCurrentGroupData } from "../../../store/adminData/adminData";
import dayjs from "dayjs";
import ImageWithSkeleton from "./ImageWithSkeleton/ImageWithSkeleton";

const AnnouncementPopupModal = ({ isLoading }) => {
  const [isClosing, setIsClosing] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const groupData = useAdminDataStore(selectCurrentGroupData);
  const userId = useUserStore(selectUserId);
  const alert = useGlobalStore((state) => state.alerts.currentAlert);
  const popupVisible = useGlobalStore((state) => state.alerts.popupVisible);
  const setPopupVisible = useGlobalStore(
    (state) => state.alerts.setPopupVisible,
  );
  const setCurrentAlert = useGlobalStore(
    (state) => state.alerts.setCurrentAlert,
  );

  const markAsViewed = async () => {
    if (!userId || !alert?.id) return;
    try {
      await setDoc(
        doc(db, "adminAlertViews", userId, "viewedAlerts", alert.id),
        {
          viewedAt: Date.now(),
          groupId: groupData?.id || null,
        },
      );
      // Invalidate all queries that fetch viewed alerts to update unread count
      queryClient.invalidateQueries({ queryKey: ["userViewedAlerts"] });
      queryClient.invalidateQueries({ queryKey: ["userViewedAlertsPanel"] });
    } catch (error) {
      console.error("Error marking alert as viewed:", error);
    }
  };

  const handleClose = () => {
    if (!alert) return;
    setIsClosing(true);
    setTimeout(() => {
      // Reset isClosing here as well so it is always cleared, even when
      // navigation (or the !alert early-return) removes the Modal from the
      // tree before afterClose has a chance to fire.
      setIsClosing(false);
      setPopupVisible(false);
      setCurrentAlert(null);
      markAsViewed();
    }, 300);
  };

  const handleCTAClick = () => {
    if (alert?.ctaUrl) {
      handleClose();
      navigate(alert.ctaUrl);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    return dayjs(timestamp).format("MMM D, YYYY");
  };

  if (isLoading || !alert || (!popupVisible && !isClosing)) {
    return null;
  }

  const isPortrait = alert.imageLayout === "portrait";
  const hasImage = !!alert.imageUrl;

  return (
    <Modal
      open={popupVisible && !isClosing}
      onCancel={handleClose}
      afterClose={() => setIsClosing(false)}
      footer={null}
      closable={false}
      width={isPortrait && hasImage ? 640 : 560}
      className={`announcement-popup-modal ${isClosing ? "ant-modal-closing" : ""}`}
      maskClosable={true}
      style={{ padding: 0 }}
      styles={{
        content: { padding: 0 },
        body: { padding: 0 },
      }}
    >
      <div className="announcement-popup-container">
        {/* Header */}
        <div className="announcement-popup-header">
          <div className="announcement-popup-header-wrapper">
            <h2 className="announcement-popup-title">{alert?.title}</h2>
            <button className="announcement-popup-close" onClick={handleClose}>
              <CloseOutlined />
            </button>
          </div>
        </div>

        {/* Body - layout depends on imageLayout field */}
        <div
          className={`announcement-popup-body ${isPortrait && hasImage ? "portrait-layout" : ""}`}
        >
          {/* Text side */}
          <div className="announcement-popup-text-content">
            <div className="announcement-popup-badge">
              <span className="announcement-badge-label">NEW UPDATE</span>
              <span className="announcement-badge-dot">•</span>
              <span className="announcement-badge-date">
                {formatDate(alert?.createdAt)}
              </span>
            </div>
            <p className="announcement-popup-description">
              {alert?.description}
            </p>
          </div>

          {/* Image side */}
          {hasImage && (
            <div
              className={`announcement-popup-image-wrapper ${isPortrait ? "portrait" : "landscape"}`}
            >
              <ImageWithSkeleton
                src={alert.imageUrl}
                alt={alert.title}
                layout={alert.imageLayout || "landscape"}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="announcement-popup-footer">
          {alert?.ctaUrl ? (
            <ChantButton type="primary" onClick={handleCTAClick}>
              {alert?.ctaText || "Go"}
            </ChantButton>
          ) : (
            <ChantButton type="primary" onClick={handleClose}>
              Got it
            </ChantButton>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AnnouncementPopupModal;
